"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface DatePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: (date: Date) => boolean
}

const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  ({
    date,
    onDateChange,
    placeholder = "Pick a date",
    disabled,
    className,
    ...props
  }, ref) => {
    return (
      <div ref={ref} {...props}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "justify-start text-left font-normal",
                !date && "text-muted-foreground",
                className
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "PPP") : <span>{placeholder}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={onDateChange}
              disabled={disabled}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)
DatePicker.displayName = "DatePicker"

interface DateTimePickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  date?: Date
  onDateChange?: (date: Date | undefined) => void
  time?: string
  onTimeChange?: (time: string) => void
  timeOptions?: string[]
  placeholder?: string
  disabled?: (date: Date) => boolean
}

const DateTimePicker = React.forwardRef<HTMLDivElement, DateTimePickerProps>(
  ({
    date,
    onDateChange,
    time = "09:00",
    onTimeChange,
    timeOptions = [
      "00:00", "01:00", "02:00", "03:00", "04:00", "05:00",
      "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
      "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
      "18:00", "19:00", "20:00", "21:00", "22:00", "23:00"
    ],
    placeholder = "Pick a date",
    disabled,
    className,
    ...props
  }, ref) => {
    return (
      <div ref={ref} className={cn("flex gap-2", className)} {...props}>
        <DatePicker
          date={date}
          onDateChange={onDateChange}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1"
        />
        <Select value={time} onValueChange={onTimeChange}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent>
            {timeOptions.map((timeOption) => (
              <SelectItem key={timeOption} value={timeOption}>
                {timeOption}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }
)
DateTimePicker.displayName = "DateTimePicker"

export { DatePicker, DateTimePicker }